import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
    Mic, 
    MicOff, 
    Loader2, 
    Volume2,
    CheckCircle,
    RotateCcw,
    Play,
    Pause
} from 'lucide-react';

/**
 * VoiceInput Component
 * Allows users to record voice input which is transcribed to text
 * 
 * Props:
 * - onTranscript: (text: string) => void - Callback when transcription is ready
 * - placeholder: string - Text to show when empty
 * - value: string - Current transcribed value
 * - disabled: boolean - Disable recording
 * - api: AxiosInstance - API instance for transcription
 * - minDuration: number - Minimum recording duration in ms (default: 1000)
 * - maxDuration: number - Maximum recording duration in ms (default: 120000)
 * - showTranscript: boolean - Show the transcribed text (default: true)
 * - autoSubmit: boolean - Auto-submit after transcription (default: false)
 * - onRecordingStart: () => void - Callback when recording starts
 * - onRecordingEnd: () => void - Callback when recording ends
 * - className: string - Additional CSS classes
 */
const VoiceInput = ({
    onTranscript,
    placeholder = "Press the microphone to speak your answer...",
    value = "",
    disabled = false,
    api,
    minDuration = 1000,
    maxDuration = 120000,
    showTranscript = true,
    autoSubmit = false,
    onRecordingStart,
    onRecordingEnd,
    className = ""
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [error, setError] = useState(null);
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const recordingTimerRef = useRef(null);
    const startTimeRef = useRef(null);
    const isRecordingRef = useRef(false);

    useEffect(() => {
        return () => {
            // Cleanup on unmount
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    const startRecording = async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Setup audio context for level monitoring
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);
            analyserRef.current.fftSize = 256;
            
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            
            const monitorLevel = () => {
                if (analyserRef.current && isRecordingRef.current) {
                    analyserRef.current.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                    setAudioLevel(average / 255);
                    animationFrameRef.current = requestAnimationFrame(monitorLevel);
                }
            };
            
            // Setup media recorder
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            startTimeRef.current = Date.now();
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(track => track.stop());
                if (audioContextRef.current) {
                    audioContextRef.current.close();
                    audioContextRef.current = null;
                }
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                
                const duration = Date.now() - startTimeRef.current;
                
                // Check minimum duration
                if (duration < minDuration) {
                    const minSeconds = Math.ceil(minDuration / 1000);
                    setError(`Recording too short. Please speak for at least ${minSeconds} second${minSeconds > 1 ? 's' : ''}.`);
                    setIsRecording(false);
                    isRecordingRef.current = false;
                    setAudioLevel(0);
                    onRecordingEnd?.();
                    return;
                }
                
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await transcribeAudio(audioBlob);
            };
            
            // Start recording
            mediaRecorder.start(1000); // Collect data every second
            setIsRecording(true);
            isRecordingRef.current = true;
            setRecordingDuration(0);
            onRecordingStart?.();
            
            // Start level monitoring
            animationFrameRef.current = requestAnimationFrame(monitorLevel);
            
            // Start duration timer
            recordingTimerRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                setRecordingDuration(elapsed);
                
                // Auto-stop at max duration
                if (elapsed >= maxDuration) {
                    stopRecording();
                }
            }, 100);
            
        } catch (err) {
            console.error('Failed to start recording:', err);
            setError("Could not access microphone. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecordingRef.current) {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            isRecordingRef.current = false;
            setAudioLevel(0);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setIsTranscribing(true);
        
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const response = await api.post('/sourcing-labs/voice/transcribe', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000 // 2 minute timeout for long recordings
            });
            
            if (response.data.transcript) {
                onTranscript(response.data.transcript);
            } else {
                setError("Could not understand the audio. Please try again.");
            }
        } catch (err) {
            console.error('Transcription failed:', err);
            if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                setError("Transcription timed out. Try speaking for a shorter duration.");
            } else if (err.response?.status === 400) {
                setError(err.response?.data?.detail || "Could not understand the audio. Please try again.");
            } else {
                setError("Failed to transcribe audio. Please check your connection and try again.");
            }
        } finally {
            setIsTranscribing(false);
            onRecordingEnd?.();
        }
    };

    const formatDuration = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const clearTranscript = () => {
        onTranscript("");
        setError(null);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Recording Controls */}
            <div className="flex items-center gap-4">
                <Button
                    type="button"
                    variant={isRecording ? "destructive" : "default"}
                    size="lg"
                    className={`relative ${isRecording ? 'animate-pulse' : ''}`}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={disabled || isTranscribing}
                    data-testid="voice-record-btn"
                >
                    {isTranscribing ? (
                        <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Transcribing...
                        </>
                    ) : isRecording ? (
                        <>
                            <MicOff className="h-5 w-5 mr-2" />
                            Stop Recording
                        </>
                    ) : (
                        <>
                            <Mic className="h-5 w-5 mr-2" />
                            Start Speaking
                        </>
                    )}
                </Button>
                
                {/* Audio Level Indicator */}
                {isRecording && (
                    <div className="flex items-center gap-2">
                        <div className="flex gap-0.5 h-8 items-end">
                            {[...Array(10)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-1 rounded-full transition-all duration-75 ${
                                        audioLevel * 10 > i 
                                            ? 'bg-red-500' 
                                            : 'bg-muted'
                                    }`}
                                    style={{ height: `${(i + 1) * 10}%` }}
                                />
                            ))}
                        </div>
                        <Badge variant="outline" className="text-xs">
                            {formatDuration(recordingDuration)}
                        </Badge>
                    </div>
                )}
                
                {/* Clear button */}
                {value && !isRecording && !isTranscribing && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearTranscript}
                        data-testid="voice-clear-btn"
                    >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Re-record
                    </Button>
                )}
            </div>

            {/* Status/Placeholder */}
            {!value && !isRecording && !isTranscribing && !error && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Volume2 className="h-4 w-4" />
                    {placeholder}
                </p>
            )}

            {/* Error Message */}
            {error && (
                <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Transcribed Text Display */}
            {showTranscript && value && (
                <div className="p-4 rounded-xl bg-muted/30 border">
                    <div className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs text-muted-foreground mb-1">Your spoken response:</p>
                            <p className="text-sm leading-relaxed">{value}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Recording Tips */}
            {isRecording && (
                <p className="text-xs text-muted-foreground">
                    Speak clearly and explain your reasoning. Click &quot;Stop Recording&quot; when done.
                </p>
            )}
        </div>
    );
};

export default VoiceInput;
